/**
 * Natural-language window selection (Phase 4).
 *
 * A deterministic pattern resolver, not an LLM: the questions analysts ask
 * of a window ("after the first goal", "best pressing spell", "when did we
 * lose territorial control?") resolve from series the workbench has ALREADY
 * loaded — tactical score, pressing index, goal annotations — so this runs
 * client-side with no new endpoint and gives the same answer every time.
 * A question that matches no pattern returns null and the caller says so.
 */
import type { Moment } from '../match/types'
import type { Halftime } from '../match/filtering'

interface TacticalScorePoint { frame: number; tactical_score: number; possession: number }
interface PressingIndexPoint { frame: number; pressing_index: number; pressing_team: number }

export interface WindowQueryContext {
  tacticalScore: TacticalScorePoint[] | null
  pressingIndex: PressingIndexPoint[] | null
  goals: Moment[]
  halftime: Halftime | null
  fps: number
  totalFrames: number
}

export interface ResolvedWindow {
  frameStart: number
  frameEnd: number
  explanation: string
}

const WINDOW_MIN = 5

function clampWindow(start: number, totalFrames: number, fps: number): { frameStart: number; frameEnd: number } {
  const span = WINDOW_MIN * 60 * fps
  const s = Math.max(0, Math.min(start, Math.max(0, totalFrames - span)))
  return { frameStart: s, frameEnd: Math.min(totalFrames, s + span) }
}

function fmt(frame: number, fps: number): string {
  const secs = Math.floor(frame / fps)
  return `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, '0')}`
}

/** Per-minute averages of a series, for rolling-window scans. */
function minuteBuckets(points: { frame: number; value: number }[], fps: number): Map<number, number> {
  const sums = new Map<number, { s: number; n: number }>()
  for (const p of points) {
    const min = Math.floor(p.frame / (fps * 60))
    const b = sums.get(min) ?? { s: 0, n: 0 }
    b.s += p.value; b.n += 1
    sums.set(min, b)
  }
  const out = new Map<number, number>()
  for (const [min, b] of sums) out.set(min, b.s / b.n)
  return out
}

/** The WINDOW_MIN-minute stretch with the extreme mean value.
 *  Returns the starting minute, or null when the series is too short. */
function extremeStretch(buckets: Map<number, number>, want: 'max' | 'min'): number | null {
  if (!buckets.size) return null
  const mins = [...buckets.keys()].sort((a, b) => a - b)
  const last = mins[mins.length - 1]!
  let bestStart: number | null = null
  let bestVal = want === 'max' ? -Infinity : Infinity
  for (let s = mins[0]!; s <= Math.max(mins[0]!, last - WINDOW_MIN + 1); s++) {
    let sum = 0, n = 0
    for (let m = s; m < s + WINDOW_MIN; m++) {
      const v = buckets.get(m)
      if (v != null) { sum += v; n++ }
    }
    if (!n) continue
    const avg = sum / n
    if (want === 'max' ? avg > bestVal : avg < bestVal) { bestVal = avg; bestStart = s }
  }
  return bestStart
}

const ORDINALS: Record<string, number> = {
  first: 0, '1st': 0, opening: 0,
  second: 1, '2nd': 1,
  third: 2, '3rd': 2,
  fourth: 3, '4th': 3,
}

export function resolveWindowQuery(q: string, ctx: WindowQueryContext): ResolvedWindow | null {
  const s = q.toLowerCase().trim()
  const { fps, totalFrames } = ctx

  // "around 34:00" / "at minute 34" / "around 34"
  const timeM = /(?:around|at|near)\s+(?:minute\s+)?(\d{1,3})(?::(\d{2}))?/.exec(s)
  if (timeM) {
    const secs = parseInt(timeM[1]!, 10) * 60 + (timeM[2] ? parseInt(timeM[2], 10) : 0)
    const center = secs * fps
    const w = clampWindow(center - (WINDOW_MIN * 60 * fps) / 2, totalFrames, fps)
    return { ...w, explanation: `Window centered on ${fmt(center, fps)}.` }
  }

  // "first/last 5 minutes (of the first/second half)"
  const edgeM = /(first|last|opening|final|closing)\s+(?:(\d+)\s+)?min/.exec(s)
  if (edgeM) {
    const atStart = edgeM[1] === 'first' || edgeM[1] === 'opening'
    const secondHalf = /second half|2nd half/.test(s)
    const firstHalf = /first half|1st half/.test(s)
    const ht = ctx.halftime
    let lo = 0, hi = totalFrames
    if (ht && firstHalf) hi = ht.startFrame
    if (ht && secondHalf) lo = ht.endFrame
    // Without a named half, "last N minutes" reads as end of match,
    // "first N minutes" as kickoff.
    const w = clampWindow(atStart ? lo : hi - WINDOW_MIN * 60 * fps, totalFrames, fps)
    const halfLabel = firstHalf ? ' of the first half' : secondHalf ? ' of the second half' : ''
    return { ...w, explanation: `${atStart ? 'Opening' : 'Closing'} ${WINDOW_MIN} minutes${halfLabel}.` }
  }

  // "after the (first|second|last) goal" / "after we scored/conceded"
  if (/goal|scored|conceded/.test(s) && ctx.goals.length) {
    const frames = ctx.goals
      .map(g => g.frame ?? g.start_frame ?? 0)
      .sort((a, b) => a - b)
    let idx = 0
    for (const [word, i] of Object.entries(ORDINALS)) {
      if (s.includes(word)) { idx = Math.min(i, frames.length - 1); break }
    }
    if (/last|latest/.test(s)) idx = frames.length - 1
    const f = frames[idx]!
    const before = /before/.test(s)
    const w = clampWindow(before ? f - WINDOW_MIN * 60 * fps : f, totalFrames, fps)
    return { ...w, explanation: `Goal at ${fmt(f, fps)} — showing the ${WINDOW_MIN} minutes ${before ? 'before' : 'after'} it.` }
  }

  // "when did we lose/gain territorial control" — the biggest shift in the
  // home-minus-away tactical balance. "Lose"/"gain" pick the direction of
  // the biggest drop or rise; who "we" is stays with the analyst.
  if (/territor|control|balance/.test(s) && ctx.tacticalScore?.length) {
    const byMin = new Map<number, { h: number; hn: number; a: number; an: number }>()
    for (const p of ctx.tacticalScore) {
      const min = Math.floor(p.frame / (fps * 60))
      const b = byMin.get(min) ?? { h: 0, hn: 0, a: 0, an: 0 }
      if (p.possession === 1) { b.h += p.tactical_score; b.hn++ } else { b.a += p.tactical_score; b.an++ }
      byMin.set(min, b)
    }
    const diff = new Map<number, number>()
    for (const [min, b] of byMin) {
      if (b.hn && b.an) diff.set(min, b.h / b.hn - b.a / b.an)
    }
    // Change in balance from one WINDOW_MIN block to the next.
    const mins = [...diff.keys()].sort((x, y) => x - y)
    if (mins.length >= 2) {
      const wantDrop = /lose|lost|drop|conced/.test(s)
      let bestMin: number | null = null
      let bestDelta = 0
      for (const m of mins) {
        const prev = diff.get(m - WINDOW_MIN) ?? diff.get(m - 1)
        const cur = diff.get(m)
        if (prev == null || cur == null) continue
        const delta = cur - prev
        if (wantDrop ? delta < bestDelta : delta > bestDelta) { bestDelta = delta; bestMin = m }
      }
      if (bestMin != null) {
        const center = bestMin * 60 * fps
        const w = clampWindow(center - (WINDOW_MIN * 60 * fps) / 2, totalFrames, fps)
        return { ...w, explanation: `Territorial balance shifted most around ${bestMin}:00 — showing ${fmt(w.frameStart, fps)}\u2013${fmt(w.frameEnd, fps)}.` }
      }
    }
    return null
  }

  // "best pressing spell" / "when did we press most"
  if (/press/.test(s) && ctx.pressingIndex?.length) {
    const worst = /worst|least|low/.test(s)
    const buckets = minuteBuckets(ctx.pressingIndex.map(p => ({ frame: p.frame, value: p.pressing_index })), fps)
    const startMin = extremeStretch(buckets, worst ? 'min' : 'max')
    if (startMin == null) return null
    const w = clampWindow(startMin * 60 * fps, totalFrames, fps)
    return { ...w, explanation: `${worst ? 'Quietest' : 'Most intense'} pressing stretch starts around ${startMin}:00.` }
  }

  return null
}
